import { useEffect, useState } from 'react';
import { Popup, Toast } from 'antd-mobile';
import { ThemeProvider } from 'styled-components';
import { packService, walletService } from '../../services/api';
import type { PackPurchaseResponse, PackTemplate } from '../../services/api/types';
import { invalidateScooterReadiness } from '../../hooks/useScooterReadiness';
import { riderLight } from '../../theme/riderTheme';
import { formatValidityHours } from '../../utils/packValidity';
import {
  Sheet,
  SheetScroll,
  SheetSticky,
  DragHandle,
  CloseButton,
  LimeRail,
  Header,
  Eyebrow,
  EyebrowMark,
  TitleRow,
  Title,
  TitleAccent,
  RightLockup,
  Section,
  SectionLabel,
  BalanceRow,
  BalanceLabel,
  BalanceValue,
  TemplateCard,
  TemplateName,
  TemplateDesc,
  StatsRow,
  StatChip,
  PriceRow,
  PriceLabel,
  PriceValue,
  InsufficientPill,
  NoticeAction,
  CTAButton,
  CTAContent,
  CTALabel,
  CTAAmount,
  CTAChevron,
  Footer,
  FooterDot,
  Notice,
  StatusContainer,
  StatusRing,
  StatusGlyph,
  StatusTitle,
  StatusBody,
  StatusKv,
  StatusButton,
  LoadingShell,
  EmptyState,
  ErrorLine,
} from '../../theme/sheetPrimitives';

interface EnergyPackPurchaseModalProps {
  visible: boolean;
  scooterVin: string;
  walletBalance: number;
  initialTemplateId?: string | null;
  energyOverageAh?: number;
  onClose: () => void;
  onSuccess: (result: PackPurchaseResponse) => void;
  /** Open the scooter-pack flow for this same scooter. Supplied by parents that
   * render both modals; without it the prerequisite notice omits its action. */
  onNeedScooterPack?: () => void;
}

const PREREQUISITE_MISSING = 'scooter_pack_prerequisite_missing';

const packTypeLabel = (t: string): string => {
  switch (t) {
    case 'energy_only':
      return 'Energy only';
    case 'time_based':
      return 'Time based';
    case 'hybrid':
      return 'Hybrid';
    default:
      return t;
  }
};

const showToast = (message: string) => {
  try {
    Toast.show(message);
  } catch {
    console.log(message);
  }
};

const EnergyPackPurchaseModal = ({
  visible,
  scooterVin,
  walletBalance,
  initialTemplateId,
  energyOverageAh = 0,
  onClose,
  onSuccess,
  onNeedScooterPack,
}: EnergyPackPurchaseModalProps) => {
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [templates, setTemplates] = useState<PackTemplate[]>([]);
  const [blocked, setBlocked] = useState<PackTemplate[]>([]);
  const [catalogNotice, setCatalogNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<PackTemplate | null>(null);
  const [result, setResult] = useState<PackPurchaseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      void loadTemplates();
    } else {
      setSelected(null);
      setResult(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, scooterVin, initialTemplateId]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      // Drop the previous catalog before awaiting. getPackCatalog propagates a
      // failed request rather than resolving empty, so anything left here would
      // survive the error and stay selectable - and after a scooter switch it
      // would belong to the previous VIN. The view is gated on `loading`, so
      // clearing costs no flash.
      setTemplates([]);
      setBlocked([]);
      setCatalogNotice(null);
      setSelected(null);
      const catalog = await packService.getPackCatalog(scooterVin);
      const res = catalog.templates;
      setTemplates(res);
      setBlocked(catalog.blocked);
      setCatalogNotice(catalog.notice);

      const requested = initialTemplateId
        ? res.find(t => t.id === initialTemplateId)
        : undefined;
      const firstAffordable = res.find(t => t.price_npr <= walletBalance);
      const next =
        requested && requested.price_npr <= walletBalance ? requested : firstAffordable;
      setSelected(next ?? null);
    } catch (err: any) {
      console.error('Failed to load energy pack templates:', err);
      setError(err?.message || 'Failed to load energy pack templates');
    } finally {
      setLoading(false);
    }
  };

  const needsScooterPack = blocked.some(
    t => t.ineligible_code === PREREQUISITE_MISSING,
  );

  const handlePurchase = async () => {
    if (!selected) return;
    try {
      setPurchasing(true);
      setError(null);
      const res = await packService.purchasePack({
        template_id: selected.id,
        scooter_vin: scooterVin,
      });
      setResult(res);
      invalidateScooterReadiness(scooterVin);
      showToast('Pack purchased');
      onSuccess(res);
    } catch (err: any) {
      console.error('Energy pack purchase failed:', err);
      setError(err?.message || 'Purchase failed');
      showToast(err?.message || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  const renderForm = () => (
    <>
      <SheetScroll>
        <Header>
          <Eyebrow>
            <EyebrowMark>E</EyebrowMark>
            Pack · Energy
          </Eyebrow>
          <TitleRow>
            <Title>
              Top up <TitleAccent>energy</TitleAccent>
            </Title>
            <RightLockup $dotColor="lime">Via wallet</RightLockup>
          </TitleRow>
        </Header>

        <Section>
          <SectionLabel>Wallet balance</SectionLabel>
          <BalanceRow>
            <BalanceLabel>Available</BalanceLabel>
            <BalanceValue>{walletService.formatCurrency(walletBalance)}</BalanceValue>
          </BalanceRow>
        </Section>

        {energyOverageAh > 0 && (
          <Notice $tone="info">
            <strong>Rollover</strong>
            {energyOverageAh.toFixed(1)} Ah of previous usage will be rolled over to the next pack(s).
          </Notice>
        )}

        {catalogNotice && (
          <Notice $tone="warn">
            <strong>Unavailable</strong>
            {catalogNotice}
          </Notice>
        )}

        {/* Keyed on packs actually blocked by the prerequisite, not on an empty
            catalog: an earlier denial can empty the catalog for a different
            reason, and this notice would then be wrong. */}
        {needsScooterPack && (
          <Notice $tone="warn">
            <strong>Blocked</strong>
            You need an active Scooter Pack to buy an Energy Pack.
            {onNeedScooterPack && (
              <div>
                <NoticeAction type="button" onClick={onNeedScooterPack}>
                  Choose a Scooter Pack
                </NoticeAction>
              </div>
            )}
          </Notice>
        )}

        <Section $delay={60}>
          <SectionLabel>Available packs</SectionLabel>
          {templates.length === 0 && blocked.length === 0 ? (
            <EmptyState>
              No energy packs are available for this scooter right now.
            </EmptyState>
          ) : (
            templates.map(template => {
              const affordable = template.price_npr <= walletBalance;
              const isSelected = selected?.id === template.id;
              const validityLabel = formatValidityHours(template.validity_hours);
              return (
                <TemplateCard
                  key={template.id}
                  type="button"
                  $selected={isSelected}
                  $affordable={affordable}
                  onClick={() => affordable && setSelected(template)}
                  disabled={!affordable}
                >
                  <TemplateName>{template.name}</TemplateName>
                  {template.description && (
                    <TemplateDesc>{template.description}</TemplateDesc>
                  )}
                  <StatsRow>
                    <StatChip>{packTypeLabel(template.pack_type)}</StatChip>
                    {template.ah_limit != null && (
                      <StatChip>{template.ah_limit} Ah</StatChip>
                    )}
                    {validityLabel && (
                      <StatChip>{validityLabel}</StatChip>
                    )}
                  </StatsRow>
                  <PriceRow>
                    <PriceLabel>Price</PriceLabel>
                    <PriceValue>
                      <small>NPR</small>
                      {(template.price_npr ?? 0).toLocaleString('en-IN')}
                    </PriceValue>
                  </PriceRow>
                  {!affordable && (
                    <InsufficientPill>Insufficient balance</InsufficientPill>
                  )}
                </TemplateCard>
              );
            })
          )}

          {/* Blocked packs are priced and shown so the rider can see what the
              obstacle is guarding, but never selectable. */}
          {blocked.map(template => {
            const validityLabel = formatValidityHours(template.validity_hours);
            return (
              <TemplateCard
                key={template.id}
                type="button"
                $selected={false}
                $affordable={false}
                disabled
              >
                <TemplateName>{template.name}</TemplateName>
                {template.description && (
                  <TemplateDesc>{template.description}</TemplateDesc>
                )}
                <StatsRow>
                  <StatChip>{packTypeLabel(template.pack_type)}</StatChip>
                  {template.ah_limit != null && (
                    <StatChip>{template.ah_limit} Ah</StatChip>
                  )}
                  {validityLabel && <StatChip>{validityLabel}</StatChip>}
                </StatsRow>
                <PriceRow>
                  <PriceLabel>Price</PriceLabel>
                  <PriceValue>
                    <small>NPR</small>
                    {(template.price_npr ?? 0).toLocaleString('en-IN')}
                  </PriceValue>
                </PriceRow>
                <InsufficientPill>
                  {template.ineligible_code === PREREQUISITE_MISSING
                    ? 'Scooter pack required'
                    : 'Unavailable'}
                </InsufficientPill>
              </TemplateCard>
            );
          })}

          {error && <ErrorLine>{error}</ErrorLine>}
        </Section>
      </SheetScroll>

      <SheetSticky>
        <CTAButton
          type="button"
          $disabled={!selected || purchasing}
          disabled={!selected || purchasing}
          onClick={handlePurchase}
        >
          <CTAContent>
            <CTALabel>{purchasing ? 'Processing' : 'Purchase'}</CTALabel>
            <CTAAmount>
              {selected ? walletService.formatCurrency(selected.price_npr) : 'NPR —'}
            </CTAAmount>
          </CTAContent>
          <CTAChevron>→</CTAChevron>
        </CTAButton>
        <Footer>
          <span>
            <FooterDot $color={riderLight.accent.ok} />
            Wallet deducted
          </span>
          <span>Pack · Energy</span>
        </Footer>
      </SheetSticky>
    </>
  );

  const renderSuccess = () => (
    <SheetScroll>
      <StatusContainer>
        <StatusRing $variant="success">
          <StatusGlyph $variant="success">✓</StatusGlyph>
        </StatusRing>
        <StatusTitle>Pack added</StatusTitle>
        <StatusBody>
          {result?.message ?? 'Your energy pack is ready to use on this scooter.'}
        </StatusBody>
        <StatusKv>
          New balance{' '}
          <strong>{walletService.formatCurrency(result?.remaining_wallet_balance)}</strong>
        </StatusKv>
        <StatusButton onClick={onClose}>Done</StatusButton>
      </StatusContainer>
    </SheetScroll>
  );

  return (
    <Popup
      visible={visible}
      onMaskClick={onClose}
      position="bottom"
      bodyStyle={{ background: 'transparent', padding: 0, boxShadow: 'none' }}
      destroyOnClose
    >
      <ThemeProvider theme={riderLight as never}>
        <Sheet>
          <LimeRail />
          <DragHandle />
          <CloseButton onClick={onClose}>×</CloseButton>
          {loading ? (
            <LoadingShell>
              <div />
            </LoadingShell>
          ) : result ? (
            renderSuccess()
          ) : (
            renderForm()
          )}
        </Sheet>
      </ThemeProvider>
    </Popup>
  );
};

export default EnergyPackPurchaseModal;
